import { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { useParams } from 'react-router-dom';
import Loading from 'common/components/loading/loading';
import { getSearchTermsById } from '../features/search-bar/searchBarSlice';
import TileGridContainer from 'features/tiles/tileGridContainer';
import Tile from 'features/tiles/tile';
import ChartBridge from 'features/charts/strategies/chart-bridge';
import ReportsTable from 'features/reports/reports-table';
import { createComponentId } from 'features/reports/functions/reports-functions';
import { metricAttributionsToChartByString } from 'features/reports/metric-attribution-mapper';
import { getSelectedAttributionModel } from 'features/attribute-model/availableAttributionModelsSlice';
import { selectIsAttributionComparisonActive } from 'features/attribute-compare-model/availableAttributionCompareModelsSlice';
import { useReportQueries } from 'features/reports/hooks/use-report-queries';
import { createAllMetricAttributionsFromArMetricRows } from 'features/advanced-reports/table/factories/advanced-report-table-row-factory';

export const Reports: FC = () => {
  const { reportType } = useParams();
  const dispatch = useDispatch();

  const { isPocModeActive } = useSelector(
    (state: RootState) => state.pocToggle
  );

  const selectedAttributionModel = useSelector((state: RootState) => {
    const selectedAttributionModel = getSelectedAttributionModel(
      state,
      isPocModeActive
    );
    return selectedAttributionModel;
  });

  const shouldDisplayAttributionComparison = useSelector((state: RootState) => {
    return selectIsAttributionComparisonActive(state);
  });

  const reportQueryResults = useReportQueries(reportType ?? '');

  const shouldDisplayChart =
    reportType === 'source' && !shouldDisplayAttributionComparison;

  const { tableValueToSearch } = useSelector((state: RootState) =>
    getSearchTermsById(state, createComponentId(reportType))
  );

  const isLoadingAll = useMemo(() => {
    return (
      reportQueryResults.main.impressions.isLoading ||
      reportQueryResults.main.calls.isLoading ||
      reportQueryResults.main.forms.isLoading ||
      reportQueryResults.main.clicks.isLoading ||
      reportQueryResults.main.cost.isLoading ||
      reportQueryResults.main.revenues.isLoading ||
      reportQueryResults.main.sales.isLoading ||
      reportQueryResults.main.opps.isLoading ||
      reportQueryResults.main.oppValues.isLoading ||
      reportQueryResults.compare.impressions.isLoading ||
      reportQueryResults.compare.calls.isLoading ||
      reportQueryResults.compare.forms.isLoading ||
      reportQueryResults.compare.clicks.isLoading ||
      reportQueryResults.compare.cost.isLoading ||
      reportQueryResults.compare.revenues.isLoading ||
      reportQueryResults.compare.sales.isLoading ||
      reportQueryResults.compare.opps.isLoading ||
      reportQueryResults.compare.oppValues.isLoading
    );
  }, [reportQueryResults]);

  const isFetchingAll = useMemo(() => {
    return (
      reportQueryResults.main.impressions.isFetching ||
      reportQueryResults.main.calls.isFetching ||
      reportQueryResults.main.forms.isFetching ||
      reportQueryResults.main.clicks.isFetching ||
      reportQueryResults.main.cost.isFetching ||
      reportQueryResults.main.revenues.isFetching ||
      reportQueryResults.main.sales.isFetching ||
      reportQueryResults.main.opps.isFetching ||
      reportQueryResults.main.oppValues.isFetching ||
      reportQueryResults.compare.impressions.isFetching ||
      reportQueryResults.compare.calls.isFetching ||
      reportQueryResults.compare.forms.isFetching ||
      reportQueryResults.compare.clicks.isFetching ||
      reportQueryResults.compare.cost.isFetching ||
      reportQueryResults.compare.revenues.isFetching ||
      reportQueryResults.compare.sales.isFetching ||
      reportQueryResults.compare.opps.isFetching ||
      reportQueryResults.compare.oppValues.isFetching
    );
  }, [reportQueryResults]);

  const allARMetricRows = useMemo(() => {
    return [
      ...reportQueryResults.main.impressions.data,
      ...reportQueryResults.main.calls.data,
      ...reportQueryResults.main.forms.data,
      ...reportQueryResults.main.clicks.data,
      ...reportQueryResults.main.cost.data,
      ...reportQueryResults.main.revenues.data,
      ...reportQueryResults.main.sales.data,
      ...reportQueryResults.main.opps.data,
      ...reportQueryResults.main.oppValues.data,
      ...reportQueryResults.compare.impressions.data,
      ...reportQueryResults.compare.calls.data,
      ...reportQueryResults.compare.forms.data,
      ...reportQueryResults.compare.clicks.data,
      ...reportQueryResults.compare.cost.data,
      ...reportQueryResults.compare.revenues.data,
      ...reportQueryResults.compare.sales.data,
      ...reportQueryResults.compare.opps.data,
      ...reportQueryResults.compare.oppValues.data
    ];
  }, [reportQueryResults]);

  const reportsChartData = useMemo(() => {
    if (allARMetricRows) {
      return metricAttributionsToChartByString(
        createAllMetricAttributionsFromArMetricRows(
          selectedAttributionModel,
          reportQueryResults.main.impressions.data,
          reportQueryResults.main.clicks.data,
          reportQueryResults.main.calls.data,
          reportQueryResults.main.forms.data,
          reportQueryResults.main.opps.data,
          reportQueryResults.main.oppValues.data,
          reportQueryResults.main.sales.data,
          reportQueryResults.main.revenues.data,
          reportQueryResults.main.cost.data
        ),
        selectedAttributionModel,
        tableValueToSearch
      );
    }
  }, [
    reportQueryResults,
    selectedAttributionModel,
    tableValueToSearch,
    allARMetricRows
  ]);

  return (
    <>
      <TileGridContainer className="">
        {shouldDisplayChart ? (
          <Tile className="grow basis-full">
            <div className="p-2.5 pb-2.5 h-92">
              {!isLoadingAll && !isFetchingAll && reportsChartData ? (
                <ChartBridge chart={reportsChartData} />
              ) : (
                <Loading />
              )}
            </div>
          </Tile>
        ) : (
          <></>
        )}
        <ReportsTable
          reportQueryResults={reportQueryResults}
          reportType={reportType}
        />
      </TileGridContainer>
    </>
  );
};
